import { useMutation } from "@tanstack/react-query";
import type { SyncRunView } from "@portal/schemas";
import { Badge, Button, Card, CardAction, CardContent, CardHeader } from "@portal/ui";
import { runSync } from "../../api/client";
import { useInvalidate, useSyncRuns } from "../../api/queries";
import { CardStatus } from "../../components/cards/CardStatus";
import { InlineError } from "../../components/InlineError";
import { CardTitleWithHint } from "../../components/Hint";
import { RefreshCw, RotateCw } from "lucide-react";

const STATUS_LABELS: Record<SyncRunView["status"], string> = {
  RUNNING: "実行中",
  SUCCEEDED: "成功",
  FAILED: "失敗",
};

/** 管理者向け: Backlog リード走査の手動実行と直近の実行状態(SPEC §5.3 / §21)。 */
export function SyncCard() {
  const runs = useSyncRuns();
  const invalidate = useInvalidate();
  const run = useMutation({
    mutationFn: (full: boolean) => runSync("backlog-lead-scan", { full }),
    onSuccess: async () => {
      await Promise.all([invalidate.syncRuns(), invalidate.candidates()]);
    },
  });
  return (
    <Card data-testid="sync-card">
      <CardHeader>
        <CardTitleWithHint hint="リード管理プロジェクトの受注・完了の課題から候補を作ります。差分同期は、前回成功してから更新された分だけを見ます。">
          Backlog リード走査
        </CardTitleWithHint>
        <CardAction className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            disabled={run.isPending}
            onClick={() => run.mutate(false)}
          >
            <RefreshCw />
            差分同期
          </Button>
          <Button size="sm" disabled={run.isPending} onClick={() => run.mutate(true)}>
            <RotateCw />
            {run.isPending ? "同期中…" : "全件同期"}
          </Button>
        </CardAction>
      </CardHeader>
      <CardContent>
        <CardStatus
          isPending={runs.isPending}
          isError={runs.isError}
          refetch={() => runs.refetch()}
        />
        <InlineError error={run.error} />
        {runs.data && runs.data.runs.length === 0 && (
          <p className="text-sm text-muted-foreground">まだ実行されていません。</p>
        )}
        <ul className="flex flex-col divide-y text-sm">
          {runs.data?.runs.slice(0, 5).map((r) => (
            <li key={r.id} className="flex flex-wrap items-center gap-2 py-2">
              <Badge
                variant={
                  r.status === "FAILED"
                    ? "destructive"
                    : r.status === "RUNNING"
                      ? "warning"
                      : "success"
                }
              >
                {STATUS_LABELS[r.status]}
              </Badge>
              <span className="whitespace-nowrap">
                {r.startedAt.replace("T", " ").slice(0, 16)}
              </span>
              <span className="text-muted-foreground">
                {r.full ? "全件" : "差分"} /{" "}
                {r.trigger === "MANUAL" ? (r.triggeredByName ?? "手動") : "定期"}
              </span>
              <span className="text-muted-foreground">
                {Object.entries(r.summary)
                  .map(([k, v]) => `${SUMMARY_LABELS[k] ?? k} ${v}`)
                  .join("、")}
              </span>
              {r.message && <span className="text-destructive">{r.message}</span>}
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

const SUMMARY_LABELS: Record<string, string> = {
  scanned: "走査",
  created: "新規",
  updated: "更新",
  unchanged: "変更なし",
  failed: "失敗",
};
